import { useSyncExternalStore } from "react";

import { DEFAULT_LOCALE, ENABLED_LOCALES } from "constants/i18n";
import { LocaleDetectionSource } from "types/i18n/LocaleDetectionSourceEnum";
import { SupportedLocale } from "types/i18n/SupportedLocaleEnum";

import { detectPreferredLocale } from "./detectPreferredLocale";
import { readStoredLocale, writeStoredLocale } from "./storedLocale";

const activeLocaleListeners = new Set<() => void>();
let activeLocale: SupportedLocale = initializeActiveLocale();

/**
 * The locale the UI renders in right now, for code outside React.
 *
 * @returns The active locale.
 */
export function getActiveLocale(): SupportedLocale {
  return activeLocale;
}

/**
 * Switches the language every subscribed component renders in, and `<html lang>` with it.
 *
 * @param params.locale - The locale to render in.
 */
export function setActiveLocale({ locale }: { locale: SupportedLocale }): void {
  if (locale === activeLocale) {
    return;
  }
  activeLocale = locale;
  document.documentElement.lang = locale;
  activeLocaleListeners.forEach((listener) => listener());
}

/**
 * Registers a callback for every language switch.
 *
 * @param listener - Called after the active locale changes.
 * @returns A function that removes the listener.
 */
export function subscribeToActiveLocale(listener: () => void): () => void {
  activeLocaleListeners.add(listener);
  return () => {
    activeLocaleListeners.delete(listener);
  };
}

/**
 * The locale the UI renders in; the caller re-renders when the visitor switches language.
 *
 * @returns The active locale.
 */
export function useActiveLocale(): SupportedLocale {
  return useSyncExternalStore(subscribeToActiveLocale, getActiveLocale);
}

/**
 * Locale argument for `Intl` formatters and `toLocale*String` calls that today pass no locale, for code
 * outside React. English keeps `undefined`, so the browser's regional format (en-GB dates, say) is kept.
 *
 * @returns `undefined` in English, otherwise the Chinese locale tag.
 */
export function getIntlLocale(): string | undefined {
  return toIntlLocale({ locale: getActiveLocale() });
}

/**
 * Hook form of `getIntlLocale`; the caller re-renders when the visitor switches language.
 *
 * @returns `undefined` in English, otherwise the Chinese locale tag.
 */
export function useIntlLocale(): string | undefined {
  return toIntlLocale({ locale: useActiveLocale() });
}

function toIntlLocale({ locale }: { locale: SupportedLocale }): string | undefined {
  return locale === SupportedLocale.English ? undefined : locale;
}

function initializeActiveLocale(): SupportedLocale {
  const detectedLocale = detectPreferredLocale({
    search: window.location.search,
    storedLocale: readStoredLocale(),
    navigatorLanguages: window.navigator.languages ?? [window.navigator.language],
  });
  // A disabled locale must not render, nor be stored from a `?lang=` link, until it is enabled again.
  if (!ENABLED_LOCALES.includes(detectedLocale.locale)) {
    document.documentElement.lang = DEFAULT_LOCALE;
    return DEFAULT_LOCALE;
  }
  // A `?lang=` link is a choice, so it outlives the URL it came on; a guessed browser language is not stored.
  if (detectedLocale.source === LocaleDetectionSource.QueryParam) {
    writeStoredLocale({ locale: detectedLocale.locale });
  }
  document.documentElement.lang = detectedLocale.locale;
  return detectedLocale.locale;
}
